import React, { Fragment, memo, useState, useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import _ from 'lodash'
import { adAccountDataTransformer } from '@/utils/adaccount-data-transformer'
import { useForm } from '@/utils/form-hook'
import { useOAuth2 } from '@/utils/oauth2-hook'
import { useTOSacceptance } from '@/utils/tos-acceptance-hook'
import { showNotification } from '@/utils/notifications'
import { toast } from 'react-toastify'
import { SettingsContext } from '@/context/settings-context'
import ShowMediaBuyPixel from '@/components/campaigns/ShowMediaBuyPixel'
import SettingsAdAccountModal from '@/components/campaigns/SettingsAdAccountModal'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { CubeLoader } from '@/components/ui/cube-loader'
import Publisher from './Publisher'
import {
    MEDIA_BUYER_AD_ACCOUNT_REFRESH,
    GET_AD_INSIGHTS,
    GET_MEDIA_BUYER_INITIAL,
    GET_TOS_STATUS,
    UPDATE_PUBLISHER_CAMPAIGN_SETTINGS,
    CONNECT_CAMPAIGN_TO_PUBLISHER,
    DISCONNECT_PUBLISHER_CAMPAIGN,
} from '@/graphql/media-buying-queries.js'
import 'react-toastify/dist/ReactToastify.css'
import 'flatpickr/dist/flatpickr.css'

const AdAccount = memo(() => {
    const { theme } = useContext(SettingsContext)
    const mediaBuyingContext = useMediaBuyingContext()
    const [supportPublishers] = useState(['Meta']) // may need to hoist this for different envs config
    const [priorityPublisher] = useState('Meta')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedPublisherToDelete, setSelectedPublisherToDelete] = useState(null)
    const [publishers, setPublishers] = useState([])

    const params = useParams()
    const campaignId = params.id
    const { getAuth } = useOAuth2({
        campaignId: campaignId,
        redirectUri: `${document.location.origin}/popup.html`,
        responseType: 'code',
        extraQueryParameters: {},
    })

    const { showTOS } = useTOSacceptance()

    const [accountType] = useState({ USER: 'USER', SYSTEM: 'SYSTEM' })
    const [statuses] = useState({ ACTIVE: 'ACTIVE', INACTIVE: 'INACTIVE' })

    const notifyMetaFirstVisit = (publisherObj) => {
        if (publisherObj?.status === statuses.ACTIVE && !localStorage.getItem('meta_ad_account_added')) {
            showNotification('Meta ad account successfully added.', 'success')
            localStorage.setItem('meta_ad_account_added', 'true')
        }
    }

    const [insightMetrics] = useState({ SPEND: 'SPEND' })

    const [showPixel, setShowPixel] = useState(false)
    const [isSystem, setIsSystem] = useState({})
    const [pixelId, setPixelId] = useState('')
    const [showAdAccountSelection, setShowAdAccountSelection] = useState(false)
    const [showTOSModal, setShowTOSModal] = useState(false)
    const [tosAccepted, setTosAccepted] = useState(null)
    const [selectedSpecialCategories, setSelectedSpecialCategories] = useState([])
    const [showSpecialAdCategoryModal, setShowSpecialAdCategoryModal] = useState(false)
    const [showSettingsAdAccountModal, setShowSettingsAdAccountModal] = useState({
        accountSelection: false,
        tosAccepted: false,
        specialAdCategory: false,
    })
    const [tosFaceBookDetails, setTosFaceBookDetails] = useState({})
    const [accountSelectionModalPublisher, setAccountSelectionModalPublisher] = useState(priorityPublisher)
    const [usersAdAccounts, setUsersAdAccounts] = useState({ loading: true })
    const [insightFetched, setInsightFetched] = useState(false)
    const [campaignData, setCampaignData] = useState(null)
    const [connectedPublishers, setConnectedPublishers] = useState({})
    const [openConnectionDialog, setOpenConnectionDialog] = useState(false)
    const [savingPublisher, setSavingPublisher] = useState(null)
    const [pollCount, setPollCount] = useState(0)
    const [syncing, setSyncing] = useState(false)
    const [accMissmatch, setAccMissmatch] = useState({})
    const [formValuesChanged, setFormValuesChanged] = useState(false)
    const [selectedPublisherData, setSelectedPublisherData] = useState({})
    const [showLoader, setShowLoader] = useState(false)

    useEffect(() => {
        setShowSettingsAdAccountModal({
            accountSelection: showAdAccountSelection,
            tosAccepted: showTOSModal,
            specialAdCategory: showSpecialAdCategoryModal,
        })
    }, [showAdAccountSelection, showSpecialAdCategoryModal, showTOSModal])

    useEffect(() => {
        if (!usersAdAccounts.loading && connectedPublishers[priorityPublisher]) {
            notifyMetaFirstVisit(connectedPublishers[priorityPublisher])
        }
    }, [usersAdAccounts.loading, connectedPublishers])

    const [startTOSPolling, { data: tosData, stopPolling }] = useLazyQuery(GET_TOS_STATUS, {
        fetchPolicy: 'network-only',
        pollInterval: 1800,
        onError: (err) => {
            console.error('TOS polling failed:', err)
            err.graphQLErrors.forEach(({ message, extensions }) => {
                if (extensions.code === 'PUBLISHER_API_ERROR') {
                    console.error('Caught publisher API error:', message)
                    showNotification(message, 'error')
                    stopPolling()
                }
            })
        },
    })

    const [refreshAccountData] = useLazyQuery(MEDIA_BUYER_AD_ACCOUNT_REFRESH, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const publisherName = data?.getUserAdAccounts[0]?.name

            if (publisherName) {
                setUsersAdAccounts((prev) => ({
                    // publisher ad account data
                    ...prev,
                    [publisherName]: [...data?.getUserAdAccounts[0].ad_accounts],
                }))

                // in case we already loaded the page without ad accounts and we now have them
                setValue((prev) => ({
                    // form values
                    ...prev,
                    [publisherName]: {
                        pixel: connectedPublishers[publisherName]?.pixel,
                        page_id: connectedPublishers[publisherName]?.page_id,
                    },
                }))
            }

            setAccMissmatch((prev) => {
                return {
                    ...prev,
                    [priorityPublisher]: !publisherName,
                }
            })

            setSyncing(false)
        },
        onError: (err) => {
            showNotification(`Error retrieving Account data: ${err.message}`, 'error')
            setAccMissmatch((prev) => {
                return {
                    ...prev,
                    [priorityPublisher]: true,
                }
            })
            setSyncing(false)
        },
    })

    const showTOSIfNotAccepted = (account_id, publisher = 'Meta') => {
        const connectedPublisher = connectedPublishers[publisher]
        const account = usersAdAccounts[publisher].find((account) => account.id === account_id)

        if (!account.tos_accepted) {
            showTOS(account_id, publisher)
            startTOSPolling({
                variables: {
                    publisherId: connectedPublisher.publisher_id,
                    campaignId,
                },
            })
        } else {
            setTosAccepted(true)
        }
    }

    useEffect(() => {
        const isAccepted = tosData?.publisherTOSStatus
        setPollCount(pollCount + 1)
        if (isAccepted || pollCount > 45) {
            setPollCount(0)
            stopPolling()
            setConnectedPublishers(
                Object.assign({}, connectedPublishers, {
                    [savingPublisher]: {
                        ...connectedPublishers[savingPublisher],
                        tos_accepted: isAccepted,
                    },
                }),
            )
            // Only update UI state if we're currently showing the TOS modal
            if (showTOSModal && !showAdAccountSelection && !showSpecialAdCategoryModal) {
                setTosAccepted(true)
            }
        } else {
            if (isAccepted === false && showTOSModal && !showAdAccountSelection && !showSpecialAdCategoryModal) {
                setTosAccepted(false)
            }
        }

        if (pollCount > 45) {
            console.log('Polling stopped after 45 attempts')
            stopPolling()
        }
    }, [tosData])

    const [savePublishedAt] = useMutation(UPDATE_PUBLISHER_CAMPAIGN_SETTINGS, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            getIntialData({
                variables: {
                    campaignId: campaignId,
                    where: {
                        id: campaignId,
                    },
                },
            })
        },
    })

    const [connectCampaignToPublisher] = useMutation(CONNECT_CAMPAIGN_TO_PUBLISHER, {
        onError({ graphQLErrors }) {
            console.log(graphQLErrors)
        },
    })

    const [disconnectPublisher] = useMutation(DISCONNECT_PUBLISHER_CAMPAIGN, {
        onError({ graphQLErrors }) {
            console.log(graphQLErrors)
        },
    })

    const [getIntialData, pageData] = useLazyQuery(GET_MEDIA_BUYER_INITIAL, {
        fetchPolicy: 'network-only',
        onCompleted: async (data) => {
            setPublishers(data.getPublishersFromPostgres)
            const { adAcc, pubAcc } = adAccountDataTransformer(data)

            setUsersAdAccounts(adAcc)
            setConnectedPublishers(pubAcc)
            setCampaignData(data?.campaigns[0])
            setIsSystem((prev) => ({
                ...prev,
                [priorityPublisher]: pubAcc[priorityPublisher]?.type === accountType.SYSTEM,
            }))

            // we dont want to show modal for selcetion for all... Just show one priority
            if (adAcc[priorityPublisher] && (!pubAcc[priorityPublisher] || !pubAcc[priorityPublisher].ad_account)) {
                if (adAcc[priorityPublisher].length === 1 && !_.isEmpty(pubAcc[priorityPublisher])) {
                    setValue((prev) => ({
                        ...prev,
                        [priorityPublisher]: {
                            pixel: adAcc[priorityPublisher][0]?.pixel?.id,
                            page_id: adAcc[priorityPublisher][0]?.page?.id,
                            label: pubAcc[priorityPublisher]?.label || '',
                        },
                    }))
                    await handleSelectedAdAccount(adAcc[priorityPublisher][0], priorityPublisher, data.getPublishersFromPostgres)
                } else {
                    if (pubAcc[priorityPublisher]) {
                        // if a publisher is connected
                        setShowAdAccountSelection(true)
                    }
                }
            }

            // handle mismatch. We have an ad_Account on the publisher but were unable to retrieve.
            if (
                pubAcc[priorityPublisher]?.ad_account &&
                !adAcc[priorityPublisher] &&
                pubAcc[priorityPublisher].type !== accountType.SYSTEM
            ) {
                showNotification(
                    `There was an issue connecting to your ad account,
                 Please click the refresh button to repair the connection. `,
                    'warning',
                )
                setAccMissmatch((prev) => {
                    return {
                        ...prev,
                        [priorityPublisher]: true,
                    }
                })
            }

            if (pubAcc[priorityPublisher]?.ad_account && adAcc[priorityPublisher]) {
                setValue({
                    ...formValues,
                    [priorityPublisher]: {
                        ...formValues[priorityPublisher],
                        ...pubAcc[priorityPublisher],
                    },
                })
            }
        },
        onError: (err) => {
            console.log(err.message)
            showNotification(err.message, 'error')
        },
    })

    const [getAdAccountInsights] = useLazyQuery(GET_AD_INSIGHTS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data.publisherAdAccountInsight) {
                const publisherName = publishers.find(
                    (pub) => pub.id === data.publisherAdAccountInsight.publisher_id,
                )?.name
                //replace the data in the connectPublisher
                connectedPublishers[publisherName] = {
                    ...connectedPublishers[publisherName],
                    spend: data.publisherAdAccountInsight.data[0].value,
                }
                setConnectedPublishers({ ...connectedPublishers })
            }
        },
    })

    // page life cycle events for handling data retrieval
    useEffect(() => {
        const getPageData = async () => {
            // Reset state to ensure clean reload
            setUsersAdAccounts({ loading: true })
            setConnectedPublishers({})
            setInsightFetched(false)

            await getIntialData({
                variables: {
                    campaignId: campaignId,
                    where: {
                        id: campaignId,
                    },
                },
            })
        }

        if (!usersAdAccounts || !campaignData) {
            getPageData()
        }
    }, [])

    // Initialize form values for all connected publishers
    useEffect(() => {
        if (!_.isEmpty(connectedPublishers)) {
            const newFormValues = {}
            Object.keys(connectedPublishers).forEach((publisherName) => {
                const publisher = connectedPublishers[publisherName]
                newFormValues[publisherName] = {
                    pixel: publisher.pixel || '',
                    page_id: publisher.page_id || '',
                    label: publisher.label || '',
                }
            })
            setValue(newFormValues)
        }
    }, [connectedPublishers])

    // update ad account insights like spend (For priority only for now)
    useEffect(() => {
        if (!_.isEmpty(connectedPublishers) && !insightFetched) {
            // get the spend data for connected publishers.
            setInsightFetched(true)
            const priorityAdAccount = connectedPublishers[priorityPublisher]
            getAdAccountInsights({
                variables: {
                    publisherAdAccountInsightInput: {
                        publisher_id: priorityAdAccount.publisher_id,
                        campaign_id: priorityAdAccount.campaign_id,
                        insights: [
                            {
                                ad_account_id: priorityAdAccount.ad_account,
                                insight_name: insightMetrics.SPEND,
                            },
                        ],
                    },
                },
            })
        }
    }, [connectedPublishers])

    // state change and mutation handlers
    const submitSettings = async (values, publisherName) => {
        const newValues = values[publisherName]
        const publisher_id = publishers.find((pub) => pub.name === publisherName)?.id

        const data = {
            publisher_id,
            campaign_id: campaignId,
            page_id: selectedPublisherData.page_id,
            pixel: selectedPublisherData.pixel,
            ...(selectedPublisherData.instagram_id && { instagram_id: selectedPublisherData.instagram_id }),
            ...(newValues.label !== undefined && { label: newValues.label ? newValues.label : null }),
        }

        const updated = await savePublishedAt({
            variables: {
                publisherCampaignSettingsInput: data,
            },
        })

        if (updated.data) {
            setConnectedPublishers({
                ...connectedPublishers,
                [publisherName]: {
                    ...connectedPublishers[publisherName],
                    ...updated.data.updatePublisherCampaignSettings,
                },
            })

            showNotification('Updated Successfully', 'success')
            // clear form values back to inital
            setValue({
                ...formValues,
                [publisherName]: {
                    ...formValues[publisherName],
                    ...updated.data.updatePublisherCampaignSettings,
                },
            })
        }
        setTimeout(resetValidation, 1500)
        setFormValuesChanged(false)
    }

    const {
        onChange,
        onSubmit,
        values: formValues,
        setValue,
        validated,
        resetValidation,
    } = useForm(submitSettings, {
        pixel: '',
        page_id: '',
        label: '',
    })

    //handles flow for ad account selection through tos
    const handleSelectedAdAccount = async (adAccount, publisherName, publishers) => {
        // Prevent multiple concurrent executions
        if (savingPublisher) return
        setSavingPublisher(publisherName)

        try {
            const publisher_id = publishers.find((pub) => pub.name === publisherName)?.id
            // if it has a pixel and a page array select the first.
            // Prefer a Page this user can actually advertise with - defaulting to an
            // unusable one silently pre-selects a Page that fails at publish time, and the
            // user never chose it so they have no reason to suspect it.
            const selectablePages = (adAccount.pages || []).filter((p) => p?.usable !== false)
            const page = _.isEmpty(selectablePages) ? { id: '', name: '' } : selectablePages[0]
            const pixel = _.isEmpty(adAccount.pixel)
                ? {
                      id: '',
                      name: '',
                  }
                : adAccount.pixel[0]

            const updated = await savePublishedAt({
                variables: {
                    publisherCampaignSettingsInput: {
                        publisher_id,
                        campaign_id: campaignId,
                        business_id: adAccount.businessId || adAccount.business_id,
                        ad_account: adAccount.id,
                        ad_account_name: adAccount.name,
                        status: statuses.ACTIVE,
                        timezone_offset: adAccount.timezone_offset,
                        timezone: adAccount.timezone,
                        page_id: page.id,
                        pixel_name: pixel.name,
                        pixel: pixel.id,
                    },
                },
            })

            // if the publisher at not found, user have not connected account yet
            if (!updated.data?.updatePublisherCampaignSettings) {
                setSavingPublisher(null)
                return
            }

            // Stop any existing polling before starting new one
            stopPolling()

            // Reset poll count to prevent interference from previous polling
            setPollCount(0)

            // start polling early. First may fail because published_at record does not exist.
            startTOSPolling({
                variables: {
                    publisherId: publisher_id,
                    campaignId,
                },
            })

            if (updated.data) {
                setConnectedPublishers({
                    ...connectedPublishers,
                    [publisherName]: {
                        ...connectedPublishers[publisherName],
                        ad_account: updated.data.updatePublisherCampaignSettings.ad_account,
                        ad_account_name: updated.data.updatePublisherCampaignSettings.ad_account_name,
                        page_id: updated.data.updatePublisherCampaignSettings.page_id,
                        pixel_name: pixel.name,
                        pixel: updated.data.updatePublisherCampaignSettings.pixel,
                        tos_accepted: updated.data.updatePublisherCampaignSettings.tos_accepted,
                        timezone_offset: updated.data.updatePublisherCampaignSettings.timezone_offset,
                        timezone: updated.data.updatePublisherCampaignSettings.timezone,
                        label: updated.data.updatePublisherCampaignSettings.label,
                        publisher_id,
                    },
                })
                // update the ad-account in our usersAdAccounts so the UI rerenders immediately
                setValue((prev) => ({
                    ...prev,
                    [publisherName]: {
                        label: updated.data.updatePublisherCampaignSettings.label,
                        pixel: pixel.id,
                        page_id: page.id,
                    },
                }))

                setShowAdAccountSelection(false)

                setTosFaceBookDetails({
                    ad_account: updated.data.updatePublisherCampaignSettings.ad_account,
                    publisherName,
                })
                setShowTOSModal(true)
                mediaBuyingContext.refetch()
            }
        } catch (error) {
            console.error('Error in handleSelectedAdAccount:', error)
            setSavingPublisher(null)
            showNotification('Failed to connect account. Please try again.', 'error')
        }
    }

    const handleTOSContinue = () => {
        //check the special_ad_category on the published_at object for the publisher
        // that was in the process of handling the connection
        setShowTOSModal(false)
        setShowAdAccountSelection(false)
        setShowSpecialAdCategoryModal(true)
    }

    const handleSpecialCategoryContinue = async () => {
        const update = await savePublishedAt({
            variables: {
                publisherCampaignSettingsInput: {
                    publisher_id: connectedPublishers[tosFaceBookDetails.publisherName]?.publisher_id,
                    campaign_id: campaignId,
                    special_ad_categories: JSON.stringify(selectedSpecialCategories),
                },
            },
        })

        if (update?.data?.updatePublisherCampaignSettings) {
            setConnectedPublishers((prev) => ({
                ...prev,
                [tosFaceBookDetails.publisherName]: {
                    ...prev[tosFaceBookDetails.publisherName],
                    special_ad_categories: selectedSpecialCategories,
                },
            }))
            setShowSpecialAdCategoryModal(false)
            setShowTOSModal(false)
            setShowAdAccountSelection(false)
            setSavingPublisher(null)
        }
    }
    const handleSpecialCategoryRevert = async (publisherName) => {
        const update = await savePublishedAt({
            variables: {
                publisherCampaignSettingsInput: {
                    publisher_id: connectedPublishers[publisherName]?.publisher_id,
                    campaign_id: campaignId,
                    ad_account: null,
                    ad_account_name: null,
                    page_id: null,
                    label: null,
                    special_ad_categories: null,
                    tos_accepted: null,
                    timezone_offset: null,
                    timezone: null,
                },
            },
        })

        if (update?.data?.updatePublisherCampaignSettings) {
            setConnectedPublishers((prev) => ({
                ...prev,
                [tosFaceBookDetails.publisherName]: {
                    ...prev[tosFaceBookDetails.publisherName],
                    ad_account: null,
                    page_id: null,
                    special_ad_categories: null,
                    tos_accepted: null,
                },
            }))
            setShowSpecialAdCategoryModal(false)
            setSavingPublisher(null) // Clear the guard so user can select again
            // Clear account data to show nothing initially in the modal
            setUsersAdAccounts((prev) => ({
                ...prev,
                [accountSelectionModalPublisher]:
                    prev[accountSelectionModalPublisher]?.map((account) => ({
                        ...account,
                        month_spend: null,
                        amount_spent: null,
                    })) || [],
            }))
            setShowAdAccountSelection(true)
        }
    }

    const getPixelCode = () => {
        return `<!-- Meta Pixel Code -->
<script>
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
</script>
<noscript>
  <img height="1" width="1" style="display:none" 
    src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>
</noscript>
<!-- End Meta Pixel Code -->
`
    }

    const onClickShowPixel = () => {
        if (pixelId) {
            setShowPixel(true)
        }
    }

    const handleDisconnect = async (connectPublisher, publisherName) => {
        await disconnectPublisher({
            variables: {
                disconnectPublisher: {
                    publisher_id: connectPublisher.publisher_id,
                    campaign_id: campaignId,
                },
            },
        })
        setConnectedPublishers((prev) => {
            const updated = { ...prev }
            delete updated[publisherName]
            return updated
        })
        setUsersAdAccounts((prev) => {
            const updated = { ...prev }
            delete updated[publisherName]
            return updated
        })
        setSavingPublisher(null)
        mediaBuyingContext.refetch()
    }

    const requestTOSClick = (connectedPublisher) => {
        if (connectedPublisher.ad_account) {
            setSavingPublisher(connectedPublisher.name)
            showTOS(connectedPublisher.ad_account, connectedPublisher.name)
            startTOSPolling({
                variables: {
                    publisherId: connectedPublisher.id,
                    campaignId,
                },
            })
        }
    }

    //bbreak
    const handleConfirm = async (selectedAccountType) => {
        if (selectedAccountType !== 'system') {
            const publisher = pageData.data.getPublishersFromPostgres.find((pub) => pub.name === selectedAccountType)
            getAuth(
                {
                    id: publisher.id,
                    authorizeUrl: publisher.authorize_url,
                    clientId: publisher.client_id,
                    scope: publisher.scope,
                },
                'publisher',
                true,
            )
        } else if (selectedAccountType === 'system') {
            setShowLoader(true)
            await connectCampaignToPublisher({
                variables: {
                    oAuthPublisherInput: {
                        id: pageData.data.getPublishersFromPostgres.find((pub) => pub.name === priorityPublisher)?.id,
                        campaignId: campaignId,
                    },
                },
            })

            await getIntialData({
                variables: {
                    campaignId: campaignId,
                    where: {
                        id: campaignId,
                    },
                },
            })
            console.log('System account selected')
            mediaBuyingContext.refetch()
        }

        setOpenConnectionDialog(false)
        setShowLoader(false)
    }

    const handleRefresh = async (selectedAccountType) => {
        if (selectedAccountType !== 'system') {
            setSyncing(true)
            toast.dismiss()
            refreshAccountData({
                variables: {
                    campaignId,
                    cache: false,
                    returnTokenErrors: true,
                },
            })
        }
    }

    const setUserOrSystemAccount = (connectedPublisher) => {
        const userOrSystem = connectedPublisher.type === 'USER' ? 'User Owned' : 'Turba Media'

        return connectedPublisher.type ? userOrSystem : 'Not Set'
    }

    return (
        <Fragment>
            {usersAdAccounts.loading || showLoader || !pageData?.data ? (
                <div className="tw:flex mx-auto tw:align-center tw:justify-center tw:h-[70vh]">
                    <CubeLoader size="default" className="" />
                </div>
            ) : (
                <Fragment>
                    {pageData.data.getPublishersFromPostgres
                        .filter((publisher) => supportPublishers.includes(publisher.name)) // todo (Tim apr-2025) remove after we add more pbulishers
                        .map((publisher) => {
                            const connectedPublisher = connectedPublishers[publisher.name]
                            const userAdAccount = (usersAdAccounts[publisher.name] || []).find(
                                (account) => String(account.id) === String(connectedPublisher?.ad_account),
                            ) || { month_spend: null, amount_spent: null }

                            return (
                                <Publisher
                                    setSelectedPublisherData={setSelectedPublisherData}
                                    key={publisher.id}
                                    publisher={publisher}
                                    connectedPublisher={connectedPublisher}
                                    theme={theme}
                                    isSystem={isSystem}
                                    handleRefresh={handleRefresh}
                                    handleConfirm={handleConfirm}
                                    setSelectedPublisherToDelete={setSelectedPublisherToDelete}
                                    setShowDeleteModal={setShowDeleteModal}
                                    openConnectionDialog={openConnectionDialog}
                                    setOpenConnectionDialog={setOpenConnectionDialog}
                                    setPixelId={setPixelId}
                                    setShowPixel={setShowPixel}
                                    resetValidation={resetValidation}
                                    onChange={onChange}
                                    formValues={formValues}
                                    savingPublisher={savingPublisher}
                                    accMissmatch={accMissmatch}
                                    userAdAccount={userAdAccount}
                                    requestTOSClick={requestTOSClick}
                                    validated={validated}
                                    setUserOrSystemAccount={setUserOrSystemAccount}
                                    submitSettings={submitSettings}
                                    syncing={syncing}
                                    allAdAccounts={usersAdAccounts[publisher.name] || []}
                                />
                            )
                        })}
                </Fragment>
            )}

            <ShowMediaBuyPixel
                show={showPixel}
                onClose={() => setShowPixel(false)}
                code={() => getPixelCode()}
                language="javascript"
            />

            <SettingsAdAccountModal
                platformName={accountSelectionModalPublisher}
                show={showSettingsAdAccountModal}
                onAdAccountConfirm={handleSelectedAdAccount}
                onAdAccountCancel={async (platformName) => {
                    stopPolling()
                    setSavingPublisher(null) // Clear the guard
                    // Clear account data to show nothing initially in future modals
                    setUsersAdAccounts((prev) => ({
                        ...prev,
                        [accountSelectionModalPublisher]:
                            prev[accountSelectionModalPublisher]?.map((account) => ({
                                ...account,
                                month_spend: null,
                                amount_spent: null,
                            })) || [],
                    }))
                    // Just close the modal, don't disconnect anything
                    setShowSettingsAdAccountModal(false)
                }}
                accounts={usersAdAccounts[accountSelectionModalPublisher]}
                //tos
                faceBookTOSRetrieval={tosFaceBookDetails}
                onAcceptOnFacebook={showTOSIfNotAccepted}
                isAccepted={tosAccepted}
                onTOSConfirm={() => {
                    stopPolling()
                    setShowTOSModal(false)
                    handleTOSContinue(tosFaceBookDetails)
                }}
                // special ad cateogories
                selectedCategories={selectedSpecialCategories}
                onToggleCategory={(value) => {
                    setSelectedSpecialCategories((prev) =>
                        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
                    )
                }}
                onSpecialCategoryConfirm={() => handleSpecialCategoryContinue()}
                onSpecialCategoryCancel={(platformName) => handleSpecialCategoryRevert(platformName)}
                publishers={publishers}
            />

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && selectedPublisherToDelete && (
                <ConfirmationModal
                    show={showDeleteModal}
                    onConfirm={async () => {
                        try {
                            // Find the publisher name from the publishers list based on publisher_id
                            const publisherName =
                                publishers.find((p) => p.id === selectedPublisherToDelete.publisher_id)?.name || 'Meta'
                            await handleDisconnect(selectedPublisherToDelete, publisherName)
                            showNotification(
                                'Account disconnected successfully. All active campaigns have been paused.',
                                'success',
                            )
                            setShowDeleteModal(false)
                            setSelectedPublisherToDelete(null)
                        } catch (error) {
                            console.error('Error disconnecting account:', error)
                            showNotification('Failed to disconnect account. Please try again.', 'error')
                        }
                    }}
                    onCancel={() => {
                        setShowDeleteModal(false)
                        setSelectedPublisherToDelete(null)
                    }}
                    title="Disconnect Ad Account"
                    body={`Are you sure you want to disconnect this ad account? This will pause all active campaigns associated with this account. Your data will be preserved and you can reconnect at any time.`}
                    confirmText="Disconnect"
                    cancelText="Cancel"
                />
            )}
        </Fragment>
    )
})

export default AdAccount
